var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Divider, Box, Collapse, Typography, Chip, } from '@mui/material';
import { Dashboard, Business, Hotel, AttachMoney, CalendarToday, BookOnline, ConnectedTv, Settings, ExpandLess, ExpandMore, Inventory, RateReview, EventAvailable, DoNotDisturb, Policy, Receipt, Webhook, } from '@mui/icons-material';
var menuStructure = [
    {
        text: 'Dashboard',
        icon: _jsx(Dashboard, {}),
        href: '/',
        type: 'single'
    },
    {
        text: 'Properties',
        icon: _jsx(Business, {}),
        href: '/properties',
        type: 'single'
    },
    {
        text: 'Inventory Management',
        icon: _jsx(Inventory, {}),
        type: 'group',
        children: [
            { text: 'Inventory Calendar', icon: _jsx(CalendarToday, {}), href: '/inventory' },
            { text: 'Room Types', icon: _jsx(Hotel, {}), href: '/room-types' },
            { text: 'Rate Plans', icon: _jsx(AttachMoney, {}), href: '/rate-plans' },
            { text: 'Availability', icon: _jsx(EventAvailable, {}), href: '/availability' },
            { text: 'Restrictions', icon: _jsx(DoNotDisturb, {}), href: '/restrictions' },
        ]
    },
    {
        text: 'Reservations',
        icon: _jsx(BookOnline, {}),
        href: '/reservations',
        type: 'single'
    },
    {
        text: 'Channels',
        icon: _jsx(ConnectedTv, {}),
        href: '/channels',
        type: 'single',
        badge: 'New'
    },
    {
        text: 'Webhooks',
        icon: _jsx(Webhook, {}),
        type: 'group',
        children: [
            { text: 'Webhook Management', icon: _jsx(Settings, {}), href: '/webhooks' },
            { text: 'Webhook Logs', icon: _jsx(Webhook, {}), href: '/webhooks_logs' },
        ]
    },
    {
        text: 'Business Rules',
        icon: _jsx(RateReview, {}),
        type: 'group',
        children: [
            { text: 'Cancellation Policies', icon: _jsx(Policy, {}), href: '/cancellation-policies' },
            { text: 'Taxes', icon: _jsx(Receipt, {}), href: '/taxes' },
        ]
    },
];
var Sidebar = function (_a) {
    var open = _a.open, onClose = _a.onClose, _b = _a.isPersistent, isPersistent = _b === void 0 ? false : _b;
    var navigate = useNavigate();
    var location = useLocation();
    var _c = useState({
        'Inventory Management': true, // Default expanded
    }), expandedGroups = _c[0], setExpandedGroups = _c[1];
    var handleNavigation = function (href) {
        navigate(href);
        onClose();
    };
    var isActive = function (href) {
        if (href === '/') {
            return location.pathname === '/';
        }
        return location.pathname.startsWith(href);
    };
    var isGroupActive = function (children) {
        return children.some(function (child) { return isActive(child.href); });
    };
    var toggleGroup = function (groupName) {
        setExpandedGroups(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[groupName] = !prev[groupName], _a)));
        });
    };
    var renderSidebarContent = function () { return (_jsxs(Box, { children: [_jsxs(Box, { className: "p-4", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsx("div", { className: "text-xl font-bold text-primary-700", children: "Channel Manager" }), _jsx(Chip, { label: "Pro", size: "small", className: "bg-gradient-to-r from-primary-500 to-primary-600 text-white font-semibold" })] }), _jsx(Typography, { variant: "body2", className: "text-gray-500 mt-1", children: "Property Management System" })] }), _jsx(Divider, {}), _jsx(List, { className: "py-2", children: menuStructure.map(function (item) { return (_jsx(Box, { children: item.type === 'single' ? (_jsx(ListItem, { disablePadding: true, className: "px-2", children: _jsxs(ListItemButton, { className: "rounded-lg mx-2 transition-all duration-200 ".concat(isActive(item.href)
                                ? 'bg-primary-100 text-primary-700 shadow-sm'
                                : 'hover:bg-gray-100 hover:shadow-sm'), onClick: function () { return handleNavigation(item.href); }, children: [_jsx(ListItemIcon, { className: "min-w-10 ".concat(isActive(item.href) ? 'text-primary-600' : 'text-gray-600'), children: item.icon }), _jsx(ListItemText, { primary: item.text, className: isActive(item.href) ? 'text-primary-700' : 'text-gray-700' }), item.badge && (_jsx(Chip, { label: item.badge, size: "small", color: "primary", className: "ml-2" }))] }) })) : (
                    // Group with expandable children
                    _jsxs(Box, { children: [_jsx(ListItem, { disablePadding: true, className: "px-2", children: _jsxs(ListItemButton, { className: "rounded-lg mx-2 transition-all duration-200 ".concat(isGroupActive(item.children)
                                        ? 'bg-primary-50 text-primary-700'
                                        : 'hover:bg-gray-50'), onClick: function () { return toggleGroup(item.text); }, children: [_jsx(ListItemIcon, { className: "min-w-10 ".concat(isGroupActive(item.children) ? 'text-primary-600' : 'text-gray-600'), children: item.icon }), _jsx(ListItemText, { primary: item.text, className: isGroupActive(item.children) ? 'text-primary-700' : 'text-gray-700' }), expandedGroups[item.text] ?
                                            _jsx(ExpandLess, { className: "text-gray-400" }) :
                                            _jsx(ExpandMore, { className: "text-gray-400" })] }) }), _jsx(Collapse, { in: expandedGroups[item.text], timeout: "auto", unmountOnExit: true, children: _jsx(List, { component: "div", disablePadding: true, className: "ml-4", children: item.children.map(function (child) { return (_jsx(ListItem, { disablePadding: true, className: "px-2", children: _jsxs(ListItemButton, { className: "rounded-lg mx-2 pl-8 transition-all duration-200 ".concat(isActive(child.href)
                                                ? 'bg-primary-100 text-primary-700 shadow-sm border-l-4 border-primary-500'
                                                : 'hover:bg-gray-100 hover:shadow-sm'), onClick: function () { return handleNavigation(child.href); }, children: [_jsx(ListItemIcon, { className: "min-w-8 ".concat(isActive(child.href) ? 'text-primary-600' : 'text-gray-500'), children: child.icon }), _jsx(ListItemText, { primary: child.text, className: isActive(child.href) ? 'text-primary-700' : 'text-gray-600' })] }) }, child.text)); }) }) })] })) }, item.text)); }) }), _jsx(Divider, { className: "my-2" }), _jsx(List, { className: "py-2", children: _jsx(ListItem, { disablePadding: true, className: "px-2", children: _jsxs(ListItemButton, { className: "rounded-lg mx-2 transition-all duration-200 ".concat(isActive('/settings')
                            ? 'bg-primary-100 text-primary-700 shadow-sm'
                            : 'hover:bg-gray-100 hover:shadow-sm'), onClick: function () { return handleNavigation('/settings'); }, children: [_jsx(ListItemIcon, { className: "min-w-10 ".concat(isActive('/settings') ? 'text-primary-600' : 'text-gray-600'), children: _jsx(Settings, {}) }), _jsx(ListItemText, { primary: "Settings", className: isActive('/settings') ? 'text-primary-700' : 'text-gray-700' })] }) }) })] })); };
    // If persistent mode, return just the content (for use within permanent Drawer)
    if (isPersistent) {
        return renderSidebarContent();
    }
    // If not persistent, return temporary Drawer
    return (_jsx(Drawer, { variant: "temporary", open: open, onClose: onClose, ModalProps: {
            keepMounted: true, // Better open performance on mobile
        }, sx: {
            '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: 280,
            },
        }, children: renderSidebarContent() }));
};
export default Sidebar;
